/**
 * Phase 39 — Cross-Tab BroadcastChannel synchronization.
 *
 * Lets multiple tabs of ATDE stay in sync without polling. When the
 * Dashboard adds a symbol in tab A, tab B receives the new list (and
 * any newly-hydrated quote) instantly via the BroadcastChannel API.
 *
 * Channels:
 *   watchlist   - {kind:'symbols'|'quote'|'row', data:...}
 *   portfolio   - {kind:'metrics'|'position', data:...}
 *   learning    - {kind:'state'|'quarantine', data:...}
 *   truth       - {kind:'audit'|'recovery', data:...}
 *   session     - {kind:'logout'|'login', data:...}
 *
 * Gracefully no-ops in browsers without BroadcastChannel.
 */
const _channels = new Map();
const _listeners = new Map();      // channel -> Set<fn>

function _ensure(channel) {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
    return null;
  }
  if (_channels.has(channel)) return _channels.get(channel);
  try {
    const bc = new BroadcastChannel(`atde.${channel}`);
    bc.onmessage = (ev) => {
      const set = _listeners.get(channel);
      if (!set) return;
      for (const fn of set) {
        try { fn(ev?.data); } catch {}
      }
    };
    _channels.set(channel, bc);
    return bc;
  } catch {
    return null;
  }
}

function publish(channel, payload) {
  const bc = _ensure(channel);
  if (bc) {
    try { bc.postMessage(payload); } catch {}
  }
}

function subscribe(channel, fn) {
  _ensure(channel);
  if (!_listeners.has(channel)) _listeners.set(channel, new Set());
  _listeners.get(channel).add(fn);
  return () => _listeners.get(channel)?.delete(fn);
}

function close(channel) {
  const bc = _channels.get(channel);
  if (bc) { try { bc.close(); } catch {} }
  _channels.delete(channel);
  _listeners.delete(channel);
}

const broadcast = { publish, subscribe, close };
export default broadcast;
